import { useId, useRef, useEffect } from 'react';
import { TH, dailyTagline } from '../lib/constants.js';
import { isPhotoUrl, flameTier } from '../lib/helpers.js';
export function Av({u,tid,sz=36,photo}){
  const t=TH[tid]||TH.neon;
  if(photo&&isPhotoUrl(photo)){
    return <div style={{width:sz,height:sz,borderRadius:"50%",flexShrink:0,boxShadow:`0 0 0 2px ${t.ac}44`,overflow:"hidden",position:"relative"}}><img src={photo} style={{width:"100%",height:"100%",objectFit:"cover"}}/></div>;
  }
  return <div style={{width:sz,height:sz,borderRadius:"50%",background:t.pill,display:"flex",alignItems:"center",justifyContent:"center",fontSize:sz*.38,fontWeight:800,color:"#fff",flexShrink:0,fontFamily:"Inter,sans-serif",boxShadow:`0 0 0 2px ${t.ac}44`}}>{u[0].toUpperCase()}</div>;
}
export function Badge({tid,sm}){
  const t=TH[tid]||TH.neon;
  return <span style={{display:"inline-flex",alignItems:"center",gap:4,padding:sm?"2px 7px":"3px 10px",borderRadius:20,background:t.ac+"22",fontSize:sm?9:10,fontFamily:"Inter,sans-serif",fontWeight:700,color:t.ac,whiteSpace:"nowrap"}}>{t.emoji} {t.name}</span>;
}
export function Logo({ac,tx,sz=30,grad}){
  const gid=useId();
  const gradId=`oncerLogoGrad${gid.replace(/:/g,"")}`;
  return(
    <div style={{display:"flex",alignItems:"center",gap:7}}>
      <svg width={sz*.62} height={sz} viewBox="0 0 26 36" fill="none">
        {grad&&(
          <defs>
            <linearGradient id={gradId} x1="2" y1="2" x2="24" y2="34" gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor={ac||"#FF2D6B"}/>
              <stop offset="1" stopColor="#7B2FFF"/>
            </linearGradient>
          </defs>
        )}
        <path d="M5 2 L21 2 A3 3 0 0 1 24 5 L24 34 L13 26 L2 34 L2 5 A3 3 0 0 1 5 2 Z" fill={grad?`url(#${gradId})`:(ac||"#FF2D6B")} stroke={grad?"#7B2FFF":(ac||"#FF2D6B")} strokeWidth="1.5" strokeLinejoin="round"/>
        <circle cx="13" cy="14" r="5.4" fill="none" stroke="rgba(255,255,255,.95)" strokeWidth="3.2"/>
      </svg>
      <div style={{display:"flex",flexDirection:"column",gap:1}}>
        <span style={{fontSize:sz*.74,fontWeight:900,color:tx||"#fff",fontFamily:"Inter,sans-serif",letterSpacing:"-1px",lineHeight:1}}>Oncer</span>
        <span style={{fontSize:sz*.26,fontWeight:400,color:tx||"#fff",fontFamily:"Inter,sans-serif",opacity:.5,lineHeight:1}}>Be a Oncer.</span>
      </div>
    </div>
  );
}
// Schermata di caricamento animata — sostituisce il Logo statico durante
// authStage==="checking" in App.jsx. Sequenza (~2.4s, poi resta in idle col
// cursore che lampeggia finché il caricamento vero non finisce, per non
// sembrare bloccata se dura più a lungo del minimo garantito SPLASH_MIN_MS):
//   1) il segnalibro si "crea" — contorno tracciato via stroke-dashoffset,
//      poi riempimento che sale dal basso via clip-path (non un semplice
//      fade, altrimenti sembrerebbe già presente e solo apparire);
//   2) un puntino bianco si anima al centro (si "dimena" — piccoli scatti
//      di posizione/schiacciamento), poi svanisce mentre al suo posto
//      compare la O vera con un piccolo balzo elastico, come se il puntino
//      "prendesse vita" e si trasformasse;
//   3) il nome "Oncer" appare lettera per lettera, poi "Be a Oncer."
//      sotto viene scritta a macchina, col cursore che continua a
//      lampeggiare a scrittura finita.
// ac/ac2 sono i due colori del tema attivo (st.myTheme, già disponibile
// subito da localStorage grazie a LS.g in App.jsx — non serve aspettare il
// profilo dal server), così il logo si colora subito col tema che l'utente
// aveva scelto l'ultima volta, anche offline. Fallback al rosa/viola di
// default se non ancora impostato (utente nuovo, primo avvio in assoluto).
// Tutte le animazioni sono imperative su ref (stesso approccio già usato
// per le transizioni tra sezioni in App.jsx) perché qui servono timer in
// sequenza precisi, non semplici transizioni CSS legate allo stato React.
export function AnimatedSplash({ac,ac2,onDone}){
  const gid=useId();
  const gradId=`splashGrad${gid.replace(/:/g,"")}`;
  const strokeRef=useRef(null);
  const fillRef=useRef(null);
  const dotRef=useRef(null);
  const ringRef=useRef(null);
  const wordRef=useRef(null);
  const tagRef=useRef(null);
  const cursorRef=useRef(null);
  // onDone (03/09/2026): segnala a chi ci usa (App.jsx) il momento VERO in cui la battitura
  // finisce — invece di fargli indovinare quanto ci metterà dalla lunghezza della frase (vedi
  // nota in App.jsx su SPLASH_MIN_MS). Tenuto in un ref, aggiornato ad ogni render SENZA passare
  // da un altro useEffect, apposta per non dover mettere onDone tra le dipendenze dell'effect qui
  // sotto: quell'effect deve restare con deps [] e girare una volta sola — una nuova funzione
  // onDone ad ogni render di App (inevitabile, è una arrow function inline) lo farebbe altrimenti
  // ripartire da capo ad ogni render, ricreando esattamente il sintomo "la frase non finisce mai".
  const onDoneRef=useRef(onDone);
  onDoneRef.current=onDone;
  useEffect(()=>{
    const strokeEl=strokeRef.current,fillEl=fillRef.current,dotEl=dotRef.current,ringEl=ringRef.current,wordEl=wordRef.current,tagEl=tagRef.current,cursorEl=cursorRef.current;
    if(!strokeEl||!fillEl||!dotEl||!ringEl||!wordEl||!tagEl||!cursorEl) return;
    // Frase del giorno (02/09/2026, richiesta di Matte): non più fissa —
    // una diversa ogni giorno, uguale per tutti finché non cambia la data,
    // scelta dall'elenco confermato (SPLASH_TAGLINES/dailyTagline in
    // constants.js — stessa funzione usata da App.jsx per calcolare
    // SPLASH_MIN_MS, così i due restano sempre in sincrono).
    const WORD="Oncer";
    const TAG=dailyTagline()+".";
    const timers=[];
    strokeEl.style.transition="none"; strokeEl.style.strokeDashoffset="1";
    fillEl.style.transition="none"; fillEl.style.clipPath="inset(100% -1% -1% -1%)";
    dotEl.style.animation="none"; dotEl.style.opacity="0"; dotEl.style.transform="translate(0,0) scale(1)";
    ringEl.style.animation="none"; ringEl.style.opacity="0"; ringEl.style.transform="scale(0)";
    wordEl.innerHTML="";
    WORD.split("").forEach(ch=>{
      const s=document.createElement("span");
      s.textContent=ch; s.style.opacity="0"; s.style.transform="translateY(6px)";
      s.style.transition="opacity .2s ease,transform .2s ease";
      wordEl.appendChild(s);
    });
    tagEl.textContent=""; cursorEl.style.animation="none";
    void strokeEl.offsetWidth; // forza il reflow prima di riattivare le transizioni sotto
    // Tempi accorciati rispetto alla prima versione: con la soglia minima
    // dello splash a 1400ms la sequenza (che durava ~2.4s solo per arrivare
    // alla O) veniva tagliata a metà su un avvio veloce. Ora l'intera
    // sequenza si esaurisce in ~2.1s, dentro il nuovo SPLASH_MIN_MS=2200 in
    // App.jsx, quindi si vede sempre per intero.
    strokeEl.style.transition="stroke-dashoffset .42s cubic-bezier(.4,0,.2,1)";
    fillEl.style.transition="clip-path .35s cubic-bezier(.4,0,.2,1)";
    timers.push(setTimeout(()=>{ strokeEl.style.strokeDashoffset="0"; },20));
    timers.push(setTimeout(()=>{ fillEl.style.clipPath="inset(-1% -1% -1% -1%)"; },350));
    timers.push(setTimeout(()=>{
      dotEl.style.opacity="1";
      dotEl.style.animation="splashDotWiggle .42s ease-in-out 1";
    },700));
    timers.push(setTimeout(()=>{
      dotEl.style.transition="opacity .16s ease,transform .16s ease";
      dotEl.style.opacity="0"; dotEl.style.transform="scale(.3)";
      ringEl.style.opacity="1";
      ringEl.style.animation="splashRingPop .38s cubic-bezier(.34,1.56,.64,1) forwards";
    },1120));
    timers.push(setTimeout(()=>{
      Array.from(wordEl.children).forEach((s,i)=>{
        timers.push(setTimeout(()=>{ s.style.opacity="1"; s.style.transform="translateY(0)"; },i*30));
      });
    },1420));
    timers.push(setTimeout(()=>{
      let i=0;
      const typeNext=()=>{
        if(i<TAG.length){ tagEl.textContent+=TAG[i]; i++; timers.push(setTimeout(typeNext,42)); }
        else{ cursorEl.style.animation="splashBlink 1s step-end infinite"; onDoneRef.current&&onDoneRef.current(); }
      };
      typeNext();
    },1650));
    return ()=>{ timers.forEach(clearTimeout); };
  },[]);
  return(
    <div style={{height:"100%",background:"#0A0A12",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
      <svg width={56} height={78} viewBox="0 0 26 36" style={{overflow:"visible"}}>
        <defs>
          <linearGradient id={gradId} x1="2" y1="2" x2="24" y2="34" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor={ac||"#FF2D6B"}/>
            <stop offset="1" stopColor={ac2||"#7B2FFF"}/>
          </linearGradient>
        </defs>
        <path ref={fillRef} d="M5 2 L21 2 A3 3 0 0 1 24 5 L24 34 L13 26 L2 34 L2 5 A3 3 0 0 1 5 2 Z" fill={`url(#${gradId})`} style={{clipPath:"inset(100% -1% -1% -1%)"}}/>
        <path ref={strokeRef} d="M5 2 L21 2 A3 3 0 0 1 24 5 L24 34 L13 26 L2 34 L2 5 A3 3 0 0 1 5 2 Z" fill="none" stroke={`url(#${gradId})`} strokeWidth="1.5" strokeLinejoin="round" pathLength="1" strokeDasharray="1.03 1" strokeDashoffset="1"/>
        <circle ref={dotRef} cx="13" cy="14" r="1.6" fill="#fff" opacity="0" style={{transformBox:"fill-box",transformOrigin:"center"}}/>
        <circle ref={ringRef} cx="13" cy="14" r="5.4" fill="none" stroke="rgba(255,255,255,.95)" strokeWidth="3.2" opacity="0" style={{transformBox:"fill-box",transformOrigin:"center",transform:"scale(0)"}}/>
      </svg>
      <div ref={wordRef} style={{marginTop:14,fontSize:26,fontWeight:900,color:"#fff",letterSpacing:"-1px",fontFamily:"Inter,sans-serif",display:"flex",lineHeight:1}}/>
      <div style={{marginTop:6,fontSize:13,color:"rgba(255,255,255,.5)",fontFamily:"Inter,sans-serif",height:16,display:"flex",lineHeight:1}}>
        <span ref={tagRef}/><span ref={cursorRef} style={{width:1,background:"rgba(255,255,255,.5)",marginLeft:1}}>&nbsp;</span>
      </div>
    </div>
  );
}
// Badge "fiamma" della serie giornaliera: grigia/spenta a 0, poi via via più
// colorata e più "vivace" (animazione più rapida/ampia) al crescere dei
// giorni di fila, col numero sempre leggibile al suo interno (vedi flameTier
// in helpers.js per le soglie e i colori di ciascun livello).
export function FlameBadge({count=0,sz=22,title}){
  const gid=useId();
  const gradId=`onceFlameGrad${gid.replace(/:/g,"")}`;
  const ft=flameTier(count);
  return(
    <div title={title} style={{position:"relative",width:sz,height:sz,flexShrink:0,display:"inline-flex",alignItems:"center",justifyContent:"center"}}>
      <svg width={sz} height={sz} viewBox="0 0 24 24" style={{position:"absolute",inset:0,transformOrigin:"50% 95%",animation:ft.anim!=="none"?`${ft.anim} ${ft.dur} ease-in-out infinite`:"none"}}>
        <defs>
          <linearGradient id={gradId} x1="4" y1="2" x2="20" y2="21" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor={ft.c1}/>
            <stop offset="1" stopColor={ft.c2}/>
          </linearGradient>
        </defs>
        <path fill={`url(#${gradId})`} d="M12 1.2 C 8.8 4.6, 5.6 7.6, 5.6 12.6 C 5.6 17.6, 8.3 21.4, 12 21.4 C 15.9 21.4, 18.4 17.6, 18.4 12.9 C 18.4 10.1, 17.1 7.6, 15.3 5.7 C 15.7 7.7, 14.6 9.7, 13.2 9.6 C 14.1 6.8, 13.4 3.6, 12 1.2 Z"/>
        <path fill={ft.cIn} opacity={ft.tier===0?0.55:0.88} d="M12.1 8.6 C 10.6 11, 9.6 13.2, 9.6 15.7 C 9.6 18.2, 10.7 19.9, 12.1 19.9 C 13.7 19.9, 14.7 18.2, 14.7 15.7 C 14.7 14.1, 14.1 12.6, 13.2 11.6 C 13.3 12.7, 12.7 13.4, 12.3 13 C 12.9 11.6, 12.6 10, 12.1 8.6 Z"/>
      </svg>
      <span style={{position:"relative",zIndex:1,fontSize:sz*.36,fontWeight:900,color:ft.num,fontFamily:"Inter,sans-serif",lineHeight:1,marginTop:sz*.24}}>{count}</span>
    </div>
  );
}
export function Btn({children,fn,style,full}){
  // cursor riflette la vera cliccabilità (fn presente o no) invece di essere
  // sempre "pointer" — prima un bottone visivamente "spento" (fn={undefined})
  // manteneva comunque il cursore a manina, rinforzando l'impressione di bug
  // quando in realtà era disabilitato di proposito (segnalato da Matte il
  // 30/08/2026). Chi passa uno style.cursor esplicito lo sovrascrive comunque,
  // essendo spreadato dopo.
  return <div className="pp" onClick={fn} aria-disabled={!fn} style={{cursor:fn?"pointer":"default",userSelect:"none",width:full?"100%":undefined,...style}}>{children}</div>;
}
export function Back({fn,t}){
  return <div onClick={fn} style={{width:32,height:32,borderRadius:"50%",background:t.card,border:`1px solid ${t.bord}`,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:14,color:t.tx,flexShrink:0}}>←</div>;
}
export function Toggle({on,onClick}){
  return(
    <div onClick={onClick} style={{width:42,height:25,borderRadius:20,background:on?"#FF2D6B":"#ddd",position:"relative",cursor:"pointer",transition:"background .2s",flexShrink:0}}>
      <div style={{position:"absolute",top:2,left:on?19:2,width:21,height:21,borderRadius:"50%",background:"#fff",boxShadow:"0 1px 3px rgba(0,0,0,.3)",transition:"left .2s"}}/>
    </div>
  );
}
export function SectionLabel({children,t}){
  return <div style={{fontSize:11,fontWeight:700,color:t?t.soft:"#999",fontFamily:"Inter,sans-serif",letterSpacing:"1px",textTransform:"uppercase",margin:"20px 4px 6px"}}>{children}</div>;
}
